import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect } from 'react';
import { ValidatorsProps } from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import useChartForm from '../../hooks/use-form';
import { useCreateCategoryMutation } from '../../store/services/categories';
import { changePanelState } from '../../store/slices/categories';
import { FormValues } from '../../types';

import ROUTES from '../../../../routes';

const useCreate = () => {
  const {
    accounts: { typeOptions },
    categories: { categories, panelIsOpen, categoryValidation },
    groups: { groups },
  } = useAppSelector((state) => state.charts);
  const { activePermissions } = useAppSelector((state) => state.root);

  const dispatch = useAppDispatch();
  const { categoryId, groupId, primaryAccountId } = useParams();

  const { control, handleSubmit, errors } = useChartForm({
    groups,
    categories,
    panelIsOpen,
    schema: categoryValidation as ValidatorsProps,
  });

  const [create, { isLoading: requestInProgress, isSuccess: onChangeSuccess }] =
    useCreateCategoryMutation();

  const navigate = useNavigate();

  const handleClose = useCallback(() => {
    dispatch(changePanelState(false));

    setTimeout(() => {
      navigate(
        generatePath(ROUTES.PRIMARY_ACCOUNTS, {
          categoryId: categoryId as string,
          groupId: groupId as string,
          primaryAccountId: primaryAccountId as string,
        }),
      );
    }, 100);
  }, [dispatch]);

  const onSubmit = handleSubmit((values: FormValues) => {
    create(values);
  });

  useEffect(() => {
    dispatch(changePanelState(true));

    return () => {
      dispatch(changePanelState(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (onChangeSuccess) {
      handleClose();
    }
  }, [onChangeSuccess, handleClose]);

  return {
    typeOptions,
    requestInProgress,
    activePermissions,
    control,
    errors,
    onSubmit,
    panelIsOpen,
    handleClose,
  };
};

export default useCreate;
